// DispatchDeck — Save Guard Utility
//
// Solves three mobile bugs that were producing duplicate records on Google Play:
//   1. Rapid taps / Enter-key + click creating duplicate Firestore documents
//      (the "6 vehicles" bug reported in Lindagrile's test account 2026-04-11)
//   2. Stuck "Saving..." spinner when Firestore writes hang on flaky networks
//      (addDoc never resolves, finally never runs)
//   3. Early returns leaking disabled button state
//
// Usage pattern (in any save function):
//
//   async function saveTruck(e) {
//     e?.preventDefault?.();
//     return runGuardedSave('saveTruck', async (markDone) => {
//       // validation, data gather, Firestore writes...
//       await withWriteTimeout(addDoc(collection(db, 'tenants', tenant, 'trucks'), data));
//       markDone({ unit: data.unit });   // optional — for soft-duplicate detection
//     }, {
//       buttonId: 'btn-save-truck',
//       restoreLabel: 'Save',
//       softDupeCheck: (prev, current) => prev?.unit && prev.unit === current?.unit
//     });
//   }

// ============================================================
// Module state
// ============================================================

const _inFlight = new Set();                  // keys currently mid-save
const _recentSaves = new Map();               // key -> { payload, timestamp }
const _handedOff = new Set();                 // keys whose UI has been released while save continues in background
const RECENT_SAVE_WINDOW_MS = 3000;           // soft-duplicate detection window
const DEFAULT_WRITE_TIMEOUT_MS = 45000;       // per-write timeout (kept generous — Firestore syncs even on bad connections)
const SAFETY_HANDOFF_MS = 20000;              // after this, release the UI and let Firestore finish in the background

// ============================================================
// Primary helper — wraps an entire save function
// ============================================================

/**
 * Run a save function with in-flight protection, button state management,
 * and a safety timer that force-restores the UI if the save hangs.
 *
 * @param {string} key — unique key for this save operation (e.g., 'saveTruck')
 * @param {(markDone: Function) => Promise<void>} saveFn — the actual save logic.
 *        Call markDone(payload) AFTER a successful Firestore write to register
 *        the save for soft-duplicate detection.
 * @param {object} opts
 * @param {string} [opts.buttonId] — DOM id of the save button (for spinner)
 * @param {string} [opts.restoreLabel='Save'] — button label to restore on completion
 * @param {(prev: object, current: object) => boolean} [opts.softDupeCheck] —
 *        optional function that receives the previous save's payload and returns
 *        true if this looks like a duplicate. If so, user is asked to confirm.
 * @param {() => object} [opts.getCurrentPayload] — used by softDupeCheck to get
 *        the current attempt's payload without running the full save first.
 */
export async function runGuardedSave(key, saveFn, opts = {}) {
  const { buttonId, restoreLabel = 'Save', softDupeCheck, getCurrentPayload } = opts;

  // ---- Step 1: In-flight lock ----
  if (_inFlight.has(key)) {
    console.warn(`[saveGuard] ${key} already in flight — ignoring duplicate invocation`);
    window.showToast?.('Save already in progress — please wait', 'info');
    return;
  }

  // ---- Step 2: Soft-duplicate check (before disabling button) ----
  if (softDupeCheck && getCurrentPayload) {
    const recent = _recentSaves.get(key);
    if (recent && (Date.now() - recent.timestamp) < RECENT_SAVE_WINDOW_MS) {
      const current = getCurrentPayload();
      if (softDupeCheck(recent.payload, current)) {
        const proceed = confirm(
          `You just created a record like this a moment ago. Are you sure you want to add another?`
        );
        if (!proceed) return;
      }
    }
  }

  _inFlight.add(key);

  // ---- Step 3: Button spinner setup ----
  const btn = buttonId ? document.getElementById(buttonId) : null;
  const originalLabel = btn?.innerHTML;
  if (btn) {
    btn.disabled = true;
    btn.dataset.saveGuardOriginalLabel = originalLabel || restoreLabel;
    btn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  // ---- Step 4: Safety handoff — if the save is still running after 20s,
  // release the UI and let Firestore finish in the background. Firestore
  // queues writes locally and retries on reconnect, so data is safe. This is
  // SAFER than rejecting with an error, which produced false "failed to save"
  // toasts for writes that actually succeeded (Zeke's 2026-04-11 bug).
  const safetyTimer = setTimeout(() => {
    _handedOff.add(key);
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = btn.dataset.saveGuardOriginalLabel || restoreLabel;
      delete btn.dataset.saveGuardOriginalLabel;
    }
    _inFlight.delete(key);
    window.showToast?.('Still saving in the background — you can keep working', 'info');
  }, SAFETY_HANDOFF_MS);

  // ---- Step 5: Run the save with mark-done callback ----
  const markDone = (payload) => {
    if (payload && typeof payload === 'object') {
      _recentSaves.set(key, { payload, timestamp: Date.now() });
      // Clean up stale entries to avoid memory growth
      if (_recentSaves.size > 50) {
        const cutoff = Date.now() - RECENT_SAVE_WINDOW_MS * 10;
        for (const [k, v] of _recentSaves.entries()) {
          if (v.timestamp < cutoff) _recentSaves.delete(k);
        }
      }
    }
  };

  try {
    await saveFn(markDone);
  } catch (err) {
    if (_handedOff.has(key)) {
      // Background handoff already messaged the user — don't double-toast
      console.warn(`[saveGuard] ${key} error after background handoff:`, err?.message || err);
    } else {
      console.error(`[saveGuard] ${key} error:`, err);
      const reason = err?.code || err?.message || 'unknown error';
      window.showToast?.(`Save failed: ${reason}`, 'error');
    }
  } finally {
    clearTimeout(safetyTimer);
    _inFlight.delete(key);
    const wasHandedOff = _handedOff.has(key);
    _handedOff.delete(key);
    if (btn && !wasHandedOff) {
      btn.disabled = false;
      btn.innerHTML = btn.dataset.saveGuardOriginalLabel || restoreLabel;
      delete btn.dataset.saveGuardOriginalLabel;
    }
  }
}

/**
 * Check whether a save key's background handoff has fired. Save functions can
 * call this before showing their own success toast, to avoid double-messaging
 * the user after the safety timer already told them the save is backgrounded.
 */
export function isHandedOff(key) {
  return _handedOff.has(key);
}

// ============================================================
// Secondary helper — wraps a single Firestore write with a timeout
// ============================================================

/**
 * Historical note: this used to race Firestore writes against a 15s timeout,
 * but that produced false "save failed" toasts when the write actually succeeded
 * (Zeke's 2026-04-11 CRM bug). Firestore has its own offline/retry machinery,
 * and on slower mobile connections the server ack can legitimately take longer
 * than 15 seconds even though the write succeeds. Racing it just rejects real
 * writes early. The runGuardedSave() safety handoff already releases the UI
 * gracefully if a save runs long, so we let the Firestore promise run to its
 * real completion. Kept as a passthrough so existing callers don't need rewrites.
 *
 * @param {Promise} promise — the Firestore operation (e.g., addDoc(...))
 * @param {number} [ms] — ignored (kept for backwards compatibility)
 * @param {string} [label] — ignored (kept for backwards compatibility)
 */
export function withWriteTimeout(promise, ms, label) {
  return promise;
}

// ============================================================
// Debug helper — inspect state from the console
// ============================================================
if (typeof window !== 'undefined') {
  window.__saveGuardState = () => ({
    inFlight: [..._inFlight],
    recentSaves: [..._recentSaves.entries()]
  });
}
